import React, { memo, type ReactElement } from 'react';
import type {
  ToolCallMessagePartComponent,
  ToolCallMessagePartStatus
} from '@assistant-ui/react';
import {
  ToolFallbackContent,
  ToolFallbackRoot,
  ToolFallbackTrigger
} from '@/components/assistant-ui/tool-fallback';
import { cn } from '@/lib/utils';
import { useMcpToolDisplayTitle } from './McpToolDisplayNames';

type ToolOutcome = {
  kind: 'running' | 'success' | 'error';
  message: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** Raw tool errors (hosts, ports, stack text) never reach the user. */
const TOOL_ERROR_MESSAGE = 'خطا در اجرای ابزار. لطفاً دوباره تلاش کنید.';

function pickMessage(obj: Record<string, unknown>): string {
  for (const key of ['msg', 'message', 'guidance', 'error'] as const) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function isApiErrorText(text: string): boolean {
  return /^MizfaTools API error \(\d+\):/m.test(text);
}

function parseToolOutcome(
  result: unknown,
  isError: boolean | undefined,
  statusType: string | undefined
): ToolOutcome {
  if (statusType === 'running' && result === undefined) {
    return { kind: 'running', message: 'در حال اجرا…' };
  }

  if (result === undefined) {
    if (statusType === 'incomplete') {
      return { kind: 'error', message: TOOL_ERROR_MESSAGE };
    }
    return { kind: 'running', message: 'در حال اجرا…' };
  }

  let obj = asRecord(result);
  let plainText: string | null = null;

  if (typeof result === 'string') {
    plainText = result.trim();
    if (plainText) {
      try {
        const parsed: unknown = JSON.parse(plainText);
        obj = asRecord(parsed);
      } catch {
        // keep plainText
      }
    }
  }

  if (obj) {
    const failed =
      isError === true || obj.ok === false || obj.isError === true;
    if (failed) return { kind: 'error', message: TOOL_ERROR_MESSAGE };
    return {
      kind: 'success',
      message: pickMessage(obj) || 'عملیات با موفقیت انجام شد'
    };
  }

  if (plainText) {
    if (isError || isApiErrorText(plainText)) {
      return { kind: 'error', message: TOOL_ERROR_MESSAGE };
    }
    return {
      kind: 'success',
      message: plainText.length > 180 ? `${plainText.slice(0, 180)}…` : plainText
    };
  }

  return {
    kind: isError ? 'error' : 'success',
    message: isError ? TOOL_ERROR_MESSAGE : 'عملیات با موفقیت انجام شد'
  };
}

function McpToolOutcomeMessage({
  outcome
}: {
  outcome: ToolOutcome;
}): ReactElement {
  return (
    <div
      data-slot="mcp-tool-outcome"
      className={cn(
        'text-xs leading-relaxed',
        outcome.kind === 'error' ? 'text-destructive' : 'text-muted-foreground'
      )}
      role={outcome.kind === 'error' ? 'alert' : 'status'}
    >
      {outcome.message}
    </div>
  );
}

/**
 * ToolFallback with Persian title and a short success/error message
 * instead of raw input/output JSON.
 */
const McpToolFallbackImpl: ToolCallMessagePartComponent = ({
  toolName,
  result,
  isError,
  status
}) => {
  const displayTitle = useMcpToolDisplayTitle(toolName);
  const isCancelled =
    status?.type === 'incomplete' && status.reason === 'cancelled';
  const outcome = isCancelled
    ? null
    : parseToolOutcome(result, isError, status?.type);
  // A failed result still arrives with status "complete"; show it as an error.
  const triggerStatus: ToolCallMessagePartStatus | undefined =
    outcome?.kind === 'error' && status?.type !== 'incomplete'
      ? { type: 'incomplete', reason: 'error' }
      : status;

  return (
    <ToolFallbackRoot defaultOpen={false}>
      <ToolFallbackTrigger toolName={displayTitle} status={triggerStatus} />
      <ToolFallbackContent>
        {outcome && <McpToolOutcomeMessage outcome={outcome} />}
      </ToolFallbackContent>
    </ToolFallbackRoot>
  );
};

export const McpToolFallback = memo(
  McpToolFallbackImpl
) as unknown as ToolCallMessagePartComponent;
